/**
 * MONSTER TOWN — ZOO ART. ZOO CREW (src/venues/zoo/**).
 *
 * The art behind the placeables declared in `src/content/animals.ts`. One
 * builder makes EVERY animal, driven entirely by the animal's data row — so a
 * new species really is a data drop, art included, which is the litmus test the
 * brief sets for venue content.
 *
 * Chunky and Lego-legible: three or four balls, a snout, two eyes, a tail. What
 * makes a rabbit a rabbit at 60 px is its EARS and its silhouette, not detail,
 * so the shape table below is all ears, snouts and stance.
 *
 * `ArtBuilder` contract as always: origin at the footprint centre, floor at
 * y = 0, nothing baked in, kit materials only.
 */

import * as THREE from 'three'
import type { ArtContext } from '../../core/placeable'
import type { PaletteName } from '../../core/palette'
import { ball, boxOn, cone, cylinder, group, inked } from '../../render/kit'

/** What an animal LOOKS like. Declared on the data row, never inferred. */
export type AnimalShape = 'hop' | 'tall' | 'waddle' | 'climb' | 'tiny' | 'big'

export interface AnimalLook {
  readonly shape: AnimalShape
  readonly body: PaletteName
  readonly belly: PaletteName
  readonly trim: PaletteName
}

const EYE_R = 0.055

/** Two eyes and a smile, on the front (+Z) face of a head of radius `r`. */
function face(g: THREE.Group, t: number, y: number, r: number): void {
  for (const s of [-1, 1]) {
    g.add(ball(t * EYE_R, { color: 'ink', x: s * r * t * 0.42, y, z: r * t * 0.86, seg: 6 }))
  }
  g.add(ball(t * 0.035, { color: 'blush', x: -r * t * 0.78, y: y - t * 0.04, z: r * t * 0.66, seg: 6 }))
  g.add(ball(t * 0.035, { color: 'blush', x: r * t * 0.78, y: y - t * 0.04, z: r * t * 0.66, seg: 6 }))
}

/**
 * ONE ANIMAL, FROM ONE ROW OF DATA.
 *
 * Everything that varies between species is in `look` plus the shape table; the
 * body plan underneath is shared, which is what keeps six animals looking like
 * they came out of the same picture book.
 */
export function animalArt(ctx: ArtContext, look: AnimalLook): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('animal')
  const body = group('body')
  g.add(body)

  const tall = look.shape === 'tall'
  const tiny = look.shape === 'tiny'
  const big = look.shape === 'big'
  const scale = tiny ? 0.68 : big ? 1.18 : 1
  const bodyR = 0.3 * scale
  const headR = 0.21 * scale
  const legH = tall ? 0.34 : 0.12

  // legs first, so the body sits ON them
  for (const x of [-1, 1]) {
    for (const z of [-1, 1]) {
      body.add(
        cylinder(t * 0.06 * scale, t * legH, {
          color: look.trim,
          x: x * bodyR * t * 0.52,
          z: z * bodyR * t * 0.5,
          seg: 6,
        }),
      )
    }
  }

  const bodyY = (legH + bodyR * 0.9) * t
  body.add(inked(ball(bodyR * t, { color: look.body, y: bodyY, seg: 10 })))
  // belly patch, slightly proud of the body so it never z-fights
  body.add(
    ball(bodyR * t * 0.7, {
      color: look.belly,
      y: bodyY - bodyR * t * 0.18,
      z: bodyR * t * 0.46,
      seg: 8,
    }),
  )

  const headY = bodyY + (tall ? bodyR * 1.5 : bodyR * 0.95) * t
  const head = group('head')
  head.position.y = headY
  body.add(head)
  head.add(inked(ball(headR * t, { color: look.body, seg: 10 })))
  face(head, t, 0, headR)
  // snout
  head.add(
    ball(headR * t * 0.42, {
      color: look.belly,
      y: -headR * t * 0.32,
      z: headR * t * 0.82,
      seg: 8,
    }),
  )
  head.add(ball(t * 0.03, { color: 'ink', y: -headR * t * 0.26, z: headR * t * 1.12, seg: 6 }))

  // -- ears: the whole silhouette ------------------------------------------
  switch (look.shape) {
    case 'hop': {
      // long upright ears
      for (const s of [-1, 1]) {
        const ear = cylinder(t * 0.05 * scale, t * 0.34 * scale, {
          color: look.body,
          x: s * headR * t * 0.5,
          y: headR * t * 0.6,
          seg: 6,
        })
        ear.rotation.z = s * 0.18
        head.add(inked(ear))
        head.add(
          ball(t * 0.045 * scale, {
            color: 'blush',
            x: s * headR * t * 0.56,
            y: headR * t * 0.6 + t * 0.3 * scale,
            z: t * 0.02,
            seg: 6,
          }),
        )
      }
      break
    }
    case 'tall': {
      // two little horns and a long neck already given by `legH`
      for (const s of [-1, 1]) {
        head.add(
          cone(t * 0.045, t * 0.14, {
            color: look.trim,
            x: s * headR * t * 0.42,
            y: headR * t * 0.68,
            seg: 6,
          }),
        )
      }
      break
    }
    case 'waddle': {
      // a flat bill instead of a snout, and no ears at all
      head.add(
        boxOn(headR * t * 1.1, t * 0.06, headR * t * 0.9, {
          color: look.trim,
          y: -headR * t * 0.4,
          z: headR * t * 0.7,
          name: 'bill',
        }),
      )
      break
    }
    case 'climb': {
      // big round side ears
      for (const s of [-1, 1]) {
        head.add(
          inked(
            ball(t * 0.09 * scale, {
              color: look.trim,
              x: s * headR * t * 1.02,
              y: headR * t * 0.16,
              seg: 8,
            }),
          ),
        )
      }
      break
    }
    case 'tiny': {
      for (const s of [-1, 1]) {
        head.add(
          ball(t * 0.075, {
            color: look.belly,
            x: s * headR * t * 0.92,
            y: headR * t * 0.5,
            seg: 8,
          }),
        )
      }
      break
    }
    case 'big': {
      for (const s of [-1, 1]) {
        head.add(
          ball(t * 0.08, {
            color: look.trim,
            x: s * headR * t * 0.86,
            y: headR * t * 0.62,
            seg: 8,
          }),
        )
      }
      break
    }
  }

  // -- tail -----------------------------------------------------------------
  const tail = group('tail')
  tail.position.set(0, bodyY, -bodyR * t * 0.95)
  if (look.shape === 'climb') {
    for (let i = 0; i < 4; i++) {
      tail.add(
        ball(t * 0.05 * scale, {
          color: look.trim,
          y: i * t * 0.09,
          z: -i * t * 0.06,
          seg: 6,
        }),
      )
    }
  } else {
    tail.add(ball(t * 0.09 * scale, { color: look.belly, seg: 8 }))
  }
  body.add(tail)

  return g
}

/**
 * ===========================================================================
 *  WHERE A PEN'S RAIL RUNS — ONE ANSWER, SHARED WITH THE GATE THAT CHECKS IT
 * ===========================================================================
 * In TILES, relative to the pen's own footprint centre. `penArt` draws it and
 * `penFitReport` (index.ts) measures animals against it, so "the animal is
 * inside its rail" is checked against the rail that was actually drawn rather
 * than against a second copy of these numbers that can drift.
 */
export const YARD_DEPTH_TILES = 1.34
/**
 * Half of the widest animal's art, in tiles. `animalArt`'s biggest body is
 * `bodyR 0.3 * 1.18` and the widest ears reach about the same, so 0.46 is that
 * with room to spare. It is the clearance a rail owes an animal standing in the
 * yard, and it is why the pens are three tiles wide (see `PEN_W`).
 */
export const ANIMAL_HALF_TILES = 0.46
/** A hair outside the footprint edge, so the rail rings the pad it belongs to. */
const RAIL_OUT_TILES = 0.06

export function penRail(
  cellsW: number,
  cellsD: number,
): { halfW: number; back: number; front: number } {
  return {
    halfW: cellsW / 2 + RAIL_OUT_TILES,
    back: -cellsD / 2,
    front: cellsD / 2 + YARD_DEPTH_TILES,
  }
}

/**
 * A HABITAT PEN — a patch of somewhere, with a YARD in front of it and a low
 * rail around the pair.
 *
 * ===========================================================================
 *  THE RAIL HAS TO BE WIDER THAN THE ANIMAL (fixed vr2 — MAJOR)
 * ===========================================================================
 * Critic, on `08-inside-the-zoo.png`: *"All four animals — bunny, penguin, bear,
 * mouse — are drawn outside their enclosure on the wrong side of the wooden
 * rail, each overlapping the fence it is supposed to be behind."* True, and the
 * arithmetic says so: a 2x2 pen's centre lands on a tile BOUNDARY, so the 1x1
 * yard cell an animal stands in is half a tile off centre. Its side rail was at
 * 0.94 of a tile and the animal's own art reaches 0.46 — so its silhouette
 * crossed the rail by about a centimetre, at every pen, every time. In front it
 * was worse: the yard ran to 1.91 tiles and the animal's nose to 1.95, so it was
 * over the FRONT rail as well. The vr1 fix ("the fence reaches out to where the
 * animal stands") was right and simply did not reach far enough.
 *
 * A rail cannot be widened past its own footprint without pens colliding, so the
 * pen got a middle column instead: `PEN_W` is 3 tiles, the starter layout puts
 * each animal in the MIDDLE one, and the maths comes out with a whole tile of
 * clearance on each side and a third of one in front. Nothing about this file
 * knows the layout — it is derived from the footprint, so a pen of any size is
 * drawn correctly and `penFitReport` measures whatever was drawn.
 *
 * The pen is the LEARNING SURFACE: a kid puts an animal in the one it wants. So
 * each habitat must be unmistakable at a glance and from across the room, which
 * means a different GROUND and a different thing standing in it, never just a
 * different colour.
 *
 * ===========================================================================
 *  THE FENCE REACHES OUT TO WHERE THE ANIMAL STANDS (fixed vr1 — MAJOR)
 * ===========================================================================
 * Critic, on `ui-04a-zoo-room.png`: *"every animal is standing on the walkway
 * OUTSIDE its pen, and all four pens are empty, so the default state shows every
 * animal in the wrong habitat with no cue that it belongs anywhere."* Which is
 * exactly what the picture shows, and it was showing it about a starter layout
 * whose own comment says the animals are ALREADY CORRECTLY SORTED. They are —
 * each one is standing directly in front of its own habitat — but a rail drawn
 * round the 2x2 put a fence between the animal and the thing it belongs to, and
 * a fence between two things is the universal picture of "these are separate".
 *
 * The animal cannot move INTO the 2x2: a pen is `walkable: false` and
 * `stackable: false`, so those cells are occupied and the grid would refuse the
 * placement — and loosening that would let a kid drop an animal on top of a
 * tree. So the YARD comes to the animal instead. The footprint is untouched
 * (still 2x2, still exactly what the grid and the placer see); only the art
 * reaches one tile forward, over the cell the starter layout puts the animal in:
 *
 *   ground pad   2x2, raised, with the tree/pond/rocks on it   (unchanged)
 *   the yard     2x1 in front, a thin mat in the same ground colour
 *   the rail     runs round BOTH, and the gate end is out past the animal
 *
 * The animal now stands on its own habitat's ground, inside its own fence. The
 * yard is 3 % of a tile tall, so nothing sinks into it. And because this is art
 * and not footprint, a kid who picks a pen up and puts it somewhere else takes
 * its yard with it, and can still stand an animal in it.
 */
export function penArt(ctx: ArtContext, habitat: string): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('pen')
  // FROM THE FOOTPRINT, NOT A CONSTANT. `w = 2 * t` was hard-coded here, so the
  // day the pens went from two tiles wide to three (see PEN_W below) the art
  // would have stayed the old size and the animal would have been standing in
  // the middle of nothing.
  const cells = Math.max(1, ctx.def.footprint.w)
  const w = cells * t
  const depth = Math.max(1, ctx.def.footprint.d) * t
  /** How far the yard reaches in front of the pen, and where its centre lands. */
  const yardDepth = t * YARD_DEPTH_TILES
  const railD = depth + yardDepth
  const railZ = yardDepth / 2

  const groundColor: PaletteName =
    habitat === 'water' ? 'water' : habitat === 'rock' ? 'stone' : habitat === 'tree' ? 'grassDark' : 'grass'
  g.add(boxOn(w * 0.99, t * 0.12, depth * 0.99, { color: groundColor, name: 'ground' }))
  // The yard: the same ground, out where the animal actually stands. Thin, so a
  // paw on it is a paw ON it and not a paw sunk into a step.
  g.add(
    boxOn(w * 0.99, t * 0.03, yardDepth * 1.02, {
      color: groundColor,
      z: depth / 2 + yardDepth * 0.5,
      name: 'yard',
    }),
  )

  // low rail all the way round the pen AND its yard, with corner posts
  const r = penRail(cells, ctx.def.footprint.d)
  const railHalfW = r.halfW * t
  const railBack = r.back * t
  const railFront = r.front * t
  for (const [x, z, sw, sd] of [
    [0, railBack, railHalfW * 2, t * 0.09],
    [0, railFront, railHalfW * 2, t * 0.09],
    [-railHalfW, railZ, t * 0.09, railD * 1.02],
    [railHalfW, railZ, t * 0.09, railD * 1.02],
  ] as [number, number, number, number][]) {
    g.add(boxOn(sw, t * 0.06, sd, { color: 'woodLight', x, y: t * 0.3, z }))
  }
  for (const x of [-1, 1]) {
    for (const z of [railBack, railFront]) {
      g.add(cylinder(t * 0.06, t * 0.4, { color: 'woodDark', x: x * railHalfW, z, seg: 6 }))
    }
  }

  const inside = group('inside')
  inside.position.y = t * 0.12
  g.add(inside)

  /*
   * ===========================================================================
   *  A PEN HAS TO HAVE SOMETHING IN IT (fixed vr2 — BLOCKER, the zoo half)
   * ===========================================================================
   * Critic: *"the four pens are flat coloured rectangles (only one has a prop in
   * it)"* — and that is what `08-inside-the-zoo.png` shows. It was not quite
   * true (all four had something) and it was true of the PICTURE, which is what
   * counts: everything except the tree pen was lying flat on the ground at 0.2
   * of a tile, on a pad two tiles across, under a camera looking down at 34
   * degrees. A lily pad seen from above is a green disc on blue; three tussocks
   * 0.24 tall are texture, not objects.
   *
   * Each habitat now stands something UP in the middle of its pad — reeds, a
   * boulder, a tree, a bush — and spreads the small stuff around it, because a
   * silhouette is what reads across a room. The pads are a tile wider than they
   * were (see `PEN_W`), which is where the room came from.
   */
  const half = w / 2
  if (habitat === 'water') {
    // a real pool with reeds standing out of it, not a disc seen from above
    inside.add(cylinder(t * 0.78, t * 0.05, { color: 'waterDeep', seg: 16, name: 'pool' }))
    inside.add(cylinder(t * 0.64, t * 0.03, { color: 'water', y: t * 0.05, seg: 16 }))
    for (const [x, z] of [
      [-0.34, 0.24],
      [0.36, -0.26],
      [0.06, 0.4],
    ] as [number, number][]) {
      inside.add(cylinder(t * 0.16, t * 0.02, { color: 'leafLight', x: x * t, y: t * 0.08, z: z * t, seg: 10 }))
    }
    for (const [x, z, hgt] of [
      [-half * 0.62, -0.2, 0.72],
      [-half * 0.5, 0.14, 0.5],
      [half * 0.6, 0.2, 0.62],
    ] as [number, number, number][]) {
      inside.add(inked(cylinder(t * 0.06, t * hgt, { color: 'leafDark', x, y: t * 0.02, z: z * t, seg: 6 })))
      inside.add(ball(t * 0.09, { color: 'leaf', x, y: t * hgt, z: z * t, seg: 6 }))
    }
    inside.add(cone(t * 0.07, t * 0.2, { color: 'flowerPink', x: t * 0.3, y: t * 0.1, z: -t * 0.24, seg: 8 }))
  } else if (habitat === 'rock') {
    // one boulder a monster could hide behind, and a scatter round it
    inside.add(inked(ball(t * 0.52, { color: 'stoneDark', x: -half * 0.3, y: t * 0.1, z: -t * 0.12, seg: 9 })))
    inside.add(inked(ball(t * 0.34, { color: 'trunkDark', x: half * 0.42, y: t * 0.06, z: t * 0.2, seg: 8 })))
    inside.add(inked(ball(t * 0.22, { color: 'stoneDark', x: t * 0.04, y: t * 0.06, z: -t * 0.42, seg: 7 })))
    inside.add(ball(t * 0.14, { color: 'trunkDark', x: -half * 0.66, y: t * 0.04, z: t * 0.34, seg: 7 }))
    inside.add(cone(t * 0.12, t * 0.22, { color: 'grassLight', x: half * 0.2, z: -t * 0.34, seg: 6 }))
  } else if (habitat === 'tree') {
    const trunk = cylinder(t * 0.14, t * 0.78, { color: 'trunk', x: -half * 0.24, seg: 7 })
    inside.add(inked(trunk))
    inside.add(inked(ball(t * 0.46, { color: 'leaf', x: -half * 0.24, y: t * 0.94, seg: 9 })))
    inside.add(ball(t * 0.28, { color: 'leafLight', x: -half * 0.24 + t * 0.28, y: t * 0.76, seg: 8 }))
    // a bush on the other side of the pad, so the pen is not one object.
    // LIGHT on the dark pad — see the contrast note in the grass branch.
    inside.add(inked(ball(t * 0.3, { color: 'leafLight', x: half * 0.5, y: t * 0.1, z: t * 0.16, seg: 8 })))
    inside.add(ball(t * 0.09, { color: 'flowerYellow', x: half * 0.5, y: t * 0.36, z: t * 0.24, seg: 6 }))
  } else {
    /*
     * CONTRAST AGAINST THE PAD, NOT AGAINST THE ROOM (vr2, second pass).
     *
     * The first go at this planted `leaf` and `grassLight` tussocks on a `grass`
     * pad. Read off the recapture at 3x (`shots/tmp-pen1b.png`): four dark
     * hairlines and an arc — every shape was within about 20/255 of the ground it
     * stood on, so all a kid could see was the ink outline, and a pen full of
     * line drawings is barely better than the empty rectangle it replaced. A
     * prop is a SILHOUETTE first; it needs a value step off whatever it stands on.
     */
    inside.add(inked(ball(t * 0.34, { color: 'leafDark', x: -half * 0.5, y: t * 0.1, z: -t * 0.1, seg: 8 })))
    for (const [x, z, c] of [
      [half * 0.34, -0.22, 'leafDark'],
      [half * 0.5, 0.26, 'grassDark'],
      [half * 0.06, -0.4, 'leafDark'],
      [-half * 0.1, 0.34, 'grassDark'],
    ] as [number, number, PaletteName][]) {
      inside.add(cone(t * 0.17, t * 0.46, { color: c, x, z: z * t, seg: 7 }))
    }
    inside.add(ball(t * 0.09, { color: 'flowerYellow', x: -half * 0.66, y: t * 0.24, z: t * 0.34, seg: 6 }))
    inside.add(ball(t * 0.09, { color: 'flowerWhite', x: half * 0.72, y: t * 0.2, z: -t * 0.3, seg: 6 }))
  }
  return g
}

/**
 * THE FEEDING TROUGH. The single most important object in the zoo: it is the
 * end of the reference errand and the way into the feeding game, so it has to
 * look like FOOD GOES HERE from any of the four camera rotations.
 */
export function feederArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('feeder')
  const w = 2 * t

  // trestle legs
  for (const s of [-1, 1]) {
    g.add(boxOn(t * 0.14, t * 0.34, t * 0.6, { color: 'woodDark', x: s * w * 0.32 }))
  }
  // the trough itself: a shallow open box, so you can see into it
  g.add(inked(boxOn(w * 0.86, t * 0.1, t * 0.72, { color: 'wood', y: t * 0.34, name: 'trough' })))
  for (const [x, z, sw, sd] of [
    [0, -t * 0.34, w * 0.86, t * 0.08],
    [0, t * 0.34, w * 0.86, t * 0.08],
    [-w * 0.43, 0, t * 0.08, t * 0.72],
    [w * 0.43, 0, t * 0.08, t * 0.72],
  ] as [number, number, number, number][]) {
    g.add(boxOn(sw, t * 0.28, sd, { color: 'woodLight', x, y: t * 0.44, z }))
  }
  // what is in it
  const food = group('food')
  food.position.y = t * 0.46
  food.add(ball(t * 0.11, { color: 'flowerRed', x: -t * 0.3, seg: 8 }))
  food.add(ball(t * 0.1, { color: 'monsterCoral', x: 0, z: t * 0.08, seg: 8 }))
  food.add(ball(t * 0.09, { color: 'roofMustard', x: t * 0.3, z: -t * 0.06, seg: 8 }))
  food.add(ball(t * 0.08, { color: 'flowerPurple', x: t * 0.12, y: t * 0.08, seg: 7 }))
  g.add(food)

  // a picture-board on a post: a bowl, so the trough says what it is for
  const post = group('sign')
  post.position.set(-w * 0.42, 0, -t * 0.42)
  post.add(cylinder(t * 0.05, t * 0.86, { color: 'woodDark', seg: 6 }))
  post.add(inked(boxOn(t * 0.44, t * 0.34, t * 0.06, { color: 'wallCream', y: t * 0.86 })))
  post.add(cylinder(t * 0.13, t * 0.03, { color: 'plate', y: t * 1.0, z: t * 0.05, seg: 12 }))
  post.add(ball(t * 0.06, { color: 'flowerRed', y: t * 1.04, z: t * 0.05, seg: 7 }))
  g.add(post)
  return g
}

/** A visitors' bench. Somewhere for a monster to sit and watch the animals. */
export function zooBenchArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('zooBench')
  const w = 2 * t
  for (const s of [-1, 1]) {
    g.add(boxOn(t * 0.14, t * 0.34, t * 0.5, { color: 'metalDark', x: s * w * 0.32 }))
  }
  g.add(inked(boxOn(w * 0.88, t * 0.1, t * 0.56, { color: 'woodLight', y: t * 0.34, name: 'seat' })))
  g.add(boxOn(w * 0.88, t * 0.32, t * 0.08, { color: 'wood', y: t * 0.44, z: -t * 0.24, name: 'back' }))
  return g
}

/**
 * THE ZOO'S FRONT GATE, in the town. Two stone piers, an arched sign with an
 * animal face on it, and a gate you walk through.
 */
export function zooGateArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('zooGate')
  const w = 3 * t
  const d = 2 * t
  const front = d * 0.42

  // the wall behind, so the gate reads as a way IN and not as a free-standing arch
  g.add(inked(boxOn(w * 0.96, t * 1.1, d * 0.5, { color: 'wallMint', z: -d * 0.2, name: 'wall' })))
  g.add(boxOn(w, t * 0.16, d * 0.56, { color: 'roofTeal', y: t * 1.1, z: -d * 0.2 }))

  // piers
  for (const s of [-1, 1]) {
    g.add(inked(boxOn(t * 0.5, t * 1.7, t * 0.5, { color: 'rock', x: s * w * 0.3, z: front * 0.4 })))
    g.add(boxOn(t * 0.62, t * 0.14, t * 0.62, { color: 'rockDark', x: s * w * 0.3, y: t * 1.7, z: front * 0.4 }))
  }
  // arch board across the top
  const board = boxOn(w * 0.74, t * 0.46, t * 0.12, {
    color: 'roofMustard',
    y: t * 1.62,
    z: front * 0.4,
    name: 'sign',
  })
  g.add(inked(board))
  // an animal face on the board — LAW #4, this is the only "word" it needs
  const faceG = group('signFace')
  faceG.position.set(0, t * 1.84, front * 0.4 + t * 0.09)
  faceG.add(ball(t * 0.17, { color: 'monsterSunny', seg: 10 }))
  for (const s of [-1, 1]) {
    faceG.add(ball(t * 0.07, { color: 'monsterCoral', x: s * t * 0.16, y: t * 0.1, seg: 7 }))
    faceG.add(ball(t * 0.025, { color: 'ink', x: s * t * 0.06, y: t * 0.02, z: t * 0.15, seg: 6 }))
  }
  g.add(faceG)

  // the gate leaves themselves
  for (const s of [-1, 1]) {
    const leaf = group(s < 0 ? 'doorL' : 'door')
    leaf.position.set(s * w * 0.14, 0, front * 0.5)
    leaf.add(boxOn(t * 0.06, t * 1.2, t * 0.06, { color: 'metalDark' }))
    for (let i = 0; i < 4; i++) {
      leaf.add(
        cylinder(t * 0.035, t * 1.06, {
          color: 'metal',
          x: s * (0.1 + i * 0.13) * t,
          seg: 5,
        }),
      )
    }
    leaf.add(boxOn(t * 0.56, t * 0.07, t * 0.07, { color: 'metalDark', x: s * t * 0.3, y: t * 1.06 }))
    g.add(leaf)
  }
  // a planter each side, so the entrance is welcoming rather than industrial
  for (const s of [-1, 1]) {
    g.add(cylinder(t * 0.2, t * 0.26, { color: 'roofRed', x: s * w * 0.44, z: front * 0.7, seg: 10 }))
    g.add(ball(t * 0.18, { color: 'leafLight', x: s * w * 0.44, y: t * 0.34, z: front * 0.7, seg: 8 }))
    g.add(ball(t * 0.07, { color: 'flowerPink', x: s * w * 0.44, y: t * 0.48, z: front * 0.72, seg: 6 }))
  }
  return g
}

/**
 * THE ZOOKEEPER. A monster in a keeper's hat standing outside the gate, holding
 * an empty bowl — which is the whole errand, said in one picture.
 */
export function keeperArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('keeper')
  const body = group('body')
  g.add(body)

  body.add(inked(ball(t * 0.3, { color: 'monsterLime', y: t * 0.32, seg: 10 })))
  body.add(ball(t * 0.2, { color: 'monsterBelly', y: t * 0.26, z: t * 0.16, seg: 8 }))
  for (const s of [-1, 1]) {
    body.add(cylinder(t * 0.07, t * 0.14, { color: 'leafDark', x: s * t * 0.14, seg: 6 }))
  }

  const head = group('head')
  head.position.y = t * 0.62
  body.add(head)
  head.add(inked(ball(t * 0.24, { color: 'monsterLime', seg: 10 })))
  for (const s of [-1, 1]) {
    head.add(ball(t * 0.05, { color: 'eyeWhite', x: s * t * 0.09, y: t * 0.02, z: t * 0.2, seg: 7 }))
    head.add(ball(t * 0.028, { color: 'pupil', x: s * t * 0.09, y: t * 0.02, z: t * 0.23, seg: 6 }))
    head.add(ball(t * 0.04, { color: 'blush', x: s * t * 0.17, y: -t * 0.04, z: t * 0.15, seg: 6 }))
  }
  // the keeper's hat — the one thing that says which monster this is
  head.add(cylinder(t * 0.19, t * 0.12, { color: 'roofTeal', y: t * 0.16, seg: 12, name: 'hat' }))
  head.add(cylinder(t * 0.3, t * 0.04, { color: 'roofTeal', y: t * 0.14, seg: 14 }))
  head.add(ball(t * 0.05, { color: 'roofMustard', y: t * 0.3, seg: 7 }))

  // the empty bowl, held out
  const bowl = group('bowl')
  bowl.position.set(t * 0.28, t * 0.42, t * 0.2)
  bowl.add(cylinder(t * 0.16, t * 0.05, { color: 'plate', seg: 12 }))
  bowl.add(cylinder(t * 0.17, t * 0.03, { color: 'glassDark', y: t * 0.05, seg: 12 }))
  g.add(bowl)
  return g
}
